import type { AppDirectClient } from '../client.js';
import { authBffDebug, refreshTokenMeta, sessionTokenMeta } from '../debug.js';
import { AuthError } from '../errors.js';
import { ensureValidFeJwt } from '../session/ensure-fejwt.js';
import { isJwtExpired } from '../session/jwt.js';
import type { RefreshCoordinator } from '../session/refresh-coordinator.js';
import type { JwtResult, ResolvedConfig } from '../types.js';

type JwtDecisionPath =
  | 'use_existing_session'
  | 'refresh_no_session'
  | 'refresh_expired_session'
  | 'will_fail_not_authenticated'
  | 'will_fail_session_expired';

function resolveJwtDecisionPath(input: {
  sessionToken: string | null;
  refreshToken: string | null;
  expiryBufferSec: number;
}): JwtDecisionPath {
  const { sessionToken, refreshToken, expiryBufferSec } = input;

  if (!sessionToken) {
    if (!refreshToken) {
      return 'will_fail_not_authenticated';
    }
    return 'refresh_no_session';
  }

  if (!isJwtExpired(sessionToken, expiryBufferSec)) {
    return 'use_existing_session';
  }

  if (!refreshToken) {
    return 'will_fail_session_expired';
  }

  return 'refresh_expired_session';
}

export async function getFeJwt(
  config: ResolvedConfig,
  client: AppDirectClient,
  coordinator: RefreshCoordinator,
  input: {
    sessionToken: string | null | undefined;
    refreshToken?: string | null;
  },
): Promise<JwtResult> {
  const sessionToken = input.sessionToken ?? null;
  const refreshToken = input.refreshToken ?? null;

  authBffDebug('jwt', 'jwt requested', {
    hasSession: sessionToken != null,
    hasRefresh: refreshToken != null,
    session: sessionTokenMeta(sessionToken, config.expiryBufferSec),
    refreshToken: refreshTokenMeta(refreshToken),
    expiryBufferSec: config.expiryBufferSec,
  });

  const path = resolveJwtDecisionPath({
    sessionToken,
    refreshToken,
    expiryBufferSec: config.expiryBufferSec,
  });

  authBffDebug('jwt', 'jwt decision', { path });

  try {
    const result = await ensureValidFeJwt(config, client, coordinator, {
      sessionToken,
      refreshToken,
    });

    authBffDebug('jwt', 'jwt succeeded', {
      path,
      tokenLength: result.token.length,
      cookieCount: result.cookiesToSet?.length ?? 0,
    });

    return result;
  } catch (err) {
    if (err instanceof AuthError) {
      authBffDebug('jwt', 'jwt failed', {
        path,
        code: err.code,
        statusCode: err.statusCode,
        details: err.details,
      });
    } else {
      authBffDebug('jwt', 'jwt failed with unexpected error', {
        path,
        name: err instanceof Error ? err.name : 'unknown',
      });
    }
    throw err;
  }
}
